import React, { Suspense, useMemo } from 'react';
import { Box, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useNavigation } from '../contexts/NavigationProvider';
import { extractAllRoutePaths, RouteItem } from '../configs/routes';
import { shouldHideNavigation } from '../configs/routeConfig';
import UnauthorizedPage from '../features/UnauthorizedPage';
import Login from '../features/login/Login';
import ProtectedRoute from '../components/shared/ProtectedRoute';
import { getMainContentStyles } from '../utils/mainContentStyles';
import AppLoader from '../components/common/AppLoader';
import NotFoundPage from '../components/common/NotFoundPage';
import OnboardingPage from '../features/onboarding/OnboardingPage';
import { useUniversityConfig } from '../contexts/UniversityConfigProvider';

const MainContent: React.FC = React.memo(() => {
  const { isSideNav, authorizedRoutes, isCollapsed } = useNavigation();
  const { config } = useUniversityConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const flattenedRoutes = useMemo(() => extractAllRoutePaths(authorizedRoutes), [authorizedRoutes]);
  const hideNavigation = useMemo(() => shouldHideNavigation(location.pathname), [location.pathname]);

  // When nav is hidden by config (e.g. demo/univ tenant), treat as top-nav so
  // sidebar margins are not applied — sidebar is not rendered in this mode.
  const effectiveIsSideNav = config?.hideNavigation ? false : isSideNav;

  const currentStyles = useMemo(
    () => getMainContentStyles(hideNavigation, isMobile, effectiveIsSideNav, isCollapsed),
    [hideNavigation, isMobile, effectiveIsSideNav, isCollapsed]
  );

  const routeElements = useMemo(
    () =>
      flattenedRoutes
        .map((route: RouteItem) => {
          if (route.path && route.component) {
            const RouteComponent = route.component;
            return (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <ProtectedRoute>
                    <RouteComponent />
                  </ProtectedRoute>
                }
              />
            );
          }
          return null;
        })
        .filter(Boolean),
    [flattenedRoutes]
  );

  const transitions = useMemo(
    () =>
      theme.transitions.create(['margin-left', 'width', 'padding-left', 'padding-right', 'padding-top'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.standard
      }),
    [theme.transitions]
  );

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        backgroundColor: theme.palette.background.default,
        minHeight: '100vh',
        boxSizing: 'border-box',
        paddingTop: currentStyles.paddingTop,
        paddingLeft: currentStyles.paddingLeft,
        paddingRight: currentStyles.paddingRight,
        paddingBottom: currentStyles.paddingBottom,
        marginLeft: currentStyles.marginLeft,
        width: currentStyles.width,
        // height: currentStyles.height || 'auto',
        transition: transitions,
        overflowX: 'hidden' 

      }}
    >
      <Suspense fallback={<AppLoader message="Loading Content" variant="default" size={50} />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/onboard" element={<OnboardingPage />} />
          {routeElements}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFoundPage />{' '}
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </Box>
  );
});

MainContent.displayName = 'MainContent';

export default MainContent;
